import type { Event } from '@sentry/react';

// Filter out known noise from third-party environments or stale-cache post-deploy errors.
export const SENTRY_IGNORE_ERRORS: (string | RegExp)[] = [
  // Facebook IAB (Android WebView): native Java object GC'd before JS bridge call.
  // Matches both "Error invoking X: Java object is gone" and the bare "Java object is gone".
  /Java object is gone/,
  // Facebook IAB (Android WebView): its navigation performance logger posts to a native
  // bridge that rejects the call ("Error invoking postMessage: Java bridge method
  // invocation error"). Thrown from iabjs:// scripts, never our code (MDP-FE-13S, 13X).
  /Java bridge method invocation error/,
  // Facebook IAB / Android WebView: Facebook injects scripts with malformed regex literals
  // into the page. Error originates at "line N, column N" of the page itself, never our JS.
  /Invalid regular expression/,
  // Facebook / Instagram IAB: a script the app injects at line 1 of the document reads
  // `window._META.IABInnerFrame` before Meta has defined it. `_META` exists nowhere in
  // our code or dependencies (MDP-FE-13K, 13P: ~100 users in October 2026).
  /IABInnerFrame/,
  // Stale cache after deploy: server returns index.html instead of an old JS chunk.
  // Using regex to cover quote character variants across browsers/OS (straight vs smart quotes).
  /Unexpected token.{0,3}</,
  // Webpack chunk load failures (handled by ErrorBoundary with auto-reload)
  'ChunkLoadError',
  // @loadable/component CSS chunk failures — same stale-cache scenario, different error code
  /CSS_CHUNK_LOAD_FAILED/,
  /Loading CSS chunk \d+ failed/,
  // Transient network errors (offline, mobile switch, Wi-Fi drop) — app recovers on its own
  'TypeError: Failed to fetch', // Chrome, Edge
  'TypeError: NetworkError when attempting to fetch resource.', // Firefox
  // Safari's wording of the same failure. Typically the page being left or
  // the iPhone app sent to the background while it is still loading: every
  // request in flight fails at once, S3 included (seen 01/10/2026 on ss,
  // 0.7 s after load, all chunks served fine). Safari's failed lazy chunk
  // is "Importing a module script failed" and is not matched here.
  'TypeError: Load failed',
  'TypeError: cancelled',
  // Apollo network error when tab is left open and connection drops
  'A network error (such as timeout, interrupted connection or unreachable host) has occurred.',
  // graphql-ws / Apollo: an operation was sent over a WebSocket that had already closed
  // (during a WS disconnect/reconnect cycle). Transient — the client reconnects and
  // re-runs the operation on its own. Non-actionable noise; also stops it consuming a replay.
  /connection already closed/,
  // Firebase Auth: user clicked login button twice — Firebase cancels the first popup automatically
  'This operation has been cancelled due to another conflicting popup being opened.',
  // Browser extension (Chromium) or Iubenda consent SDK race condition — not our code
  /TrackerStorageType is not defined/,
  // Iubenda consent SDK: references a global from a dependent script that may not have loaded yet.
  // Same class of error as TrackerStorageType — third-party load-order issue, not our code.
  'LIDNotifyId is not defined',
  // Firebase Auth: email link expired/already used — handled by AuthLink with user-friendly message
  /The action code is invalid/,
  // Link scanners of Microsoft Outlook / Defender (Safe Links): their headless browser
  // opens the links in our emails and rejects with this object from its own automation
  // code. Not a user, not our code (247 events on ss by 02/10/2026).
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
  // Facebook IAB / iOS WKWebView: third-party native bridge call with missing webkit handler.
  // window.webkit.messageHandlers is never referenced in our code — always injected environment.
  /window\.webkit\.messageHandlers/,
  // Firebase Auth: user closed the Google sign-in popup before completing login — not an error
  'The popup has been closed by the user before finalizing the operation.',
  // subscriptions-transport-ws: a WS message arrived truncated or corrupted (typically during
  // a WS reconnect with a large in-flight response). Apollo retries automatically — no action needed.
  /Message must be JSON-parseable\./,
  // MRAID ad-container bridge injected by an in-app browser (seen 08/10/2026 on ss,
  // LinkedIn's WebView following a paid-social ad click: utm_source=linkedin + li_ed).
  // The host app pushes the IAB MRAID init sequence — fireEvent, then the geometry
  // setters, then fireChangeEvent — into every page it opens, assuming an ad creative
  // with an `mraid`/`mraidbridge` global. On a normal page that global does not exist,
  // so each page load throws ~6 times in a row. None of these identifiers exist in our
  // code or in any dependency (checked), so matching the bare name is safe and covers
  // every browser's wording of the same undefined-property read. Only these errors are
  // dropped, not the whole session: LinkedIn ad traffic is the paid campaign landing on
  // the ECM courses, and genuine errors from those visitors must stay visible.
  /\b(?:fireEvent|fireChangeEvent|fireErrorEvent|fireReadyEvent|setScreenSize|setMaxSize|setDefaultPosition|setCurrentPosition|setPlacementType|setExpandProperties|setSupports)\b/,
  // The visitor's device is out of disk: IndexedDB (Firebase, Apollo persistence) cannot
  // open or write its files. Nothing we can act on (MDP-FE-13T, 13V).
  /FILE_ERROR_NO_SPACE/,
  /Encountered full disk/,
  // The visitor cleared site data while the page was open, deleting the IndexedDB
  // databases under it (MDP-FE-10T).
  /Database deleted by request of the user/,
];

// Scripts injected by Meta's in-app browsers run from iabjs:// URLs (browser_declutter,
// navigation_performance_logger_android, ...). Any error whose top frame is there is
// theirs (MDP-FE-12V, 13M, 13N, 13S).
export const SENTRY_DENY_URLS: (string | RegExp)[] = [/^iabjs:\/\//];

// Our own code is always served from the Vite build's /assets/ folder.
const OWN_BUNDLE_FRAME = /\/assets\/[^/?#]+\.js/;

export const beforeSend = (event: Event): Event | null => {
  // In SDK 7 `event.breadcrumbs` is already an array here. This used to read
  // `event.breadcrumbs?.values`, which is `Array.prototype.values`: every rule below
  // keyed on breadcrumbs was skipped from February to November 2026.
  const breadcrumbs = Array.isArray(event.breadcrumbs) ? event.breadcrumbs : [];

  // Errors raised by scripts that Facebook's in-app browser (WebView) injects into the page.
  // Two detection strategies for the session:
  // 1. FB-specific console messages in breadcrumbs (hxp-chat bridge, FBNavFirstContentfulPaint)
  // 2. fbclid query param in any navigation/fetch breadcrumb URL (FB ad traffic)
  //
  // NOTE: Sentry console breadcrumbs store the first console.log argument in
  // b.data.arguments[0] rather than b.message (which may be undefined). Both paths are checked.
  //
  // Only errors with a stack and no frame in our bundle are dropped, never the whole
  // session: those visitors are the paid campaigns landing on the courses, and our own
  // failures in front of them must stay visible. Events without frames (captureMessage,
  // DOMExceptions from IndexedDB) are kept for the same reason.
  const FB_SIGNALS = [
    'FBNavFirstContentfulPaint',
    'hxp-chat-suppression',
    'IAB unified bridge',
    // Facebook IAB page-translation script injected into in-app browser sessions
    'PageTranslationRequiredListener',
  ];
  const matchesSignal = (s: string) =>
    FB_SIGNALS.some((sig) => s.includes(sig));
  const isFacebookWebView =
    breadcrumbs.some((b) => {
      if (b.message && matchesSignal(b.message)) return true;
      const firstArg = b.data?.arguments?.[0];
      if (typeof firstArg === 'string' && matchesSignal(firstArg)) return true;
      return false;
    }) || breadcrumbs.some((b) => b.data?.['url']?.includes('fbclid'));
  if (isFacebookWebView) {
    const frames =
      event.exception?.values?.flatMap((e) => e.stacktrace?.frames ?? []) ?? [];
    const thrownOutsideOurCode =
      frames.length > 0 &&
      !frames.some((f) => OWN_BUNDLE_FRAME.test(f.filename ?? ''));
    if (thrownOutsideOurCode) return null;
  }

  // Handle "Network Error" contextually to avoid both noise and blindness:
  // 1. Apollo/axios + WS disconnect → transient connectivity drop, drop entirely.
  // 2. axios ECONNABORTED (timeout of Nms exceeded) + WS disconnect → same scenario
  //    (e.g. GetStream markRead timing out during a connectivity blip), drop entirely.
  // 3. Firebase Auth (securetoken.googleapis.com token refresh) → not actionable,
  //    but downgrade to 'info' so volume spikes remain visible in stats without
  //    generating noise. If the stack ever changes (new Firebase bug), it still shows up.
  // 4. All other Network Errors → keep as-is (genuine backend failures stay visible).
  const isNetworkError =
    event.exception?.values?.some((e) => e.value === 'Network Error') ?? false;
  const isAxiosTimeout =
    event.exception?.values?.some(
      (e) =>
        typeof e.value === 'string' &&
        /timeout of \d+ms exceeded/.test(e.value),
    ) ?? false;
  if (isNetworkError || isAxiosTimeout) {
    const hasWsDisconnect = breadcrumbs.some(
      (b) =>
        b.category === 'console' &&
        (b.message === 'WS on disconnected' ||
          b.data?.arguments?.[0] === 'WS on disconnected'),
    );
    if (hasWsDisconnect) return null;

    // ChunkLoadError page reload: ErrorBoundary triggers window.location.reload()
    // → all in-flight requests abort → Apollo/axios raises "Network Error".
    // Root cause (ChunkLoadError) is already in ignoreErrors — drop this cascade too.
    const hasChunkLoadError = breadcrumbs.some((b) => {
      const arg0 = b.data?.arguments?.[0];
      return (
        (typeof arg0 === 'string' && arg0.includes('ChunkLoadError')) ||
        (typeof b.message === 'string' && b.message.includes('ChunkLoadError'))
      );
    });
    if (hasChunkLoadError) return null;
  }

  // Firebase Auth — downgrade only the TRANSIENT / non-actionable failures to 'info'.
  // Principle: stay visible on errors that actually block the user, silence the noise
  // that the SDK recovers from on its own. Everything NOT in this list (e.g.
  // auth/user-disabled, auth/internal-error, auth/invalid-api-key) keeps its 'error'
  // level so genuine, user-blocking auth problems remain visible.
  //
  // Covers two scenarios:
  //  - Stack filename @firebase/auth (dev / source-mapped builds): securetoken token
  //    refresh failures + Safari IndexedDB "UnknownError: Connection to Indexed Database
  //    server lost" — both transient, no auth/* code in the value. Must live outside the
  //    isNetworkError block: Safari throws UnknownError, not "Network Error".
  //  - Exception type FirebaseError + a transient auth/* code (production minified
  //    bundles, where the @firebase/auth filename is lost): e.g. a connectivity drop
  //    (tab left open, Wi-Fi/mobile switch, laptop sleep). Firebase retries automatically.
  const TRANSIENT_FIREBASE_AUTH_CODES = [
    'auth/network-request-failed',
    'auth/timeout',
    'auth/too-many-requests',
    'auth/web-storage-unsupported',
  ];
  const isTransientFirebaseAuth = event.exception?.values?.some(
    (e) =>
      e.stacktrace?.frames?.some((f) =>
        f.filename?.includes('@firebase/auth'),
      ) ||
      (e.type === 'FirebaseError' &&
        typeof e.value === 'string' &&
        TRANSIENT_FIREBASE_AUTH_CODES.some((code) =>
          e.value!.includes(code),
        )) ||
      // Safari/mobile IndexedDB handle reclaimed after the tab is suspended in the
      // background (Firebase/Apollo persistence): "UnknownError: Connection to Indexed
      // Database server lost. Refresh the page to try again." Matched by value because
      // production minified bundles strip the @firebase/auth filename, so the first
      // branch above misses it in prod. Transient — resolves on refresh, non-actionable.
      (typeof e.value === 'string' &&
        /Connection to Indexed Database server lost/.test(e.value)),
  );
  if (isTransientFirebaseAuth) {
    // Drop entirely (return null) rather than downgrade to 'info'. With
    // replaysOnErrorSampleRate: 1.0, the Replay integration flushes a session
    // replay for ANY event carrying an exception — its isErrorEvent() keys on the
    // presence of an exception, NOT on event.level. So downgrading to 'info' still
    // consumed a replay (wasting quota) because beforeSendEvent fires after beforeSend
    // with the exception still attached. Returning null prevents beforeSendEvent from
    // firing at all → no replay is captured for these transient, non-actionable errors.
    return null;
  }

  // Firebase/Google Cloud transient 503 UNAVAILABLE (e.g. "Visibility check was unavailable")
  // — infrastructure noise outside app control, downgrade to info.
  const isFirebaseUnavailable = event.exception?.values?.some(
    (e) =>
      typeof e.value === 'string' && e.value.includes('"status":"UNAVAILABLE"'),
  );
  if (isFirebaseUnavailable) {
    // Same rationale as the transient Firebase Auth block above: this is
    // non-actionable infrastructure noise (Google Cloud 503 UNAVAILABLE) outside our
    // control. Drop it (return null) instead of downgrading to 'info' so it neither
    // clutters the dashboard nor consumes a session replay via replaysOnErrorSampleRate.
    return null;
  }

  return event;
};
